import React from "react";
import { FormControl, FormLabel } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RenderHTML } from "components";

import { ErrorMessage, TextAreaComponent } from "../../shared/styled-components";
import { useCommentOnlyQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface CommentOnlyQuestionProps {
  questionId: string;
  questionText: string;
  isMandatory: boolean;
  questionHint?: string | null;
  commentValue: string;
  showMandatoryError: boolean;
  showMinCharError: boolean;
  showMaxCharError: boolean;
  minCharacters?: number;
  maxCharacters?: number;
  onCommentChange: (comment: string) => void;
}

export const CommentOnlyQuestion: React.FC<CommentOnlyQuestionProps> = ({
  questionId,
  questionText,
  isMandatory,
  questionHint,
  commentValue,
  showMandatoryError,
  showMinCharError,
  showMaxCharError,
  minCharacters,
  maxCharacters,
  onCommentChange,
}) => {
  const classes = useCommentOnlyQuestionStyles();
  const { t } = useTranslation();

  const handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onCommentChange(event.target.value);
  };

  const textareaId = `textarea-${questionId}`;
  const mandatoryErrorId = `${textareaId}-mandatory-error`;
  const minCharErrorId = `${textareaId}-minchar-error`;
  const maxCharErrorId = `${textareaId}-maxchar-error`;

  // Build aria-describedby string with all active error IDs
  const errorIds = [
    showMandatoryError && isMandatory && mandatoryErrorId,
    showMinCharError && minCharErrorId,
    showMaxCharError && maxCharErrorId,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes.container}>
      <FormControl component="fieldset" aria-disabled="false" fullWidth>
        <FormLabel
          component="label"
          htmlFor={textareaId}
          className={classes.formLabel}
          aria-label={isMandatory ? `${questionText} ${ErrorMessages.InputRequired}` : questionText}
        >
          <div className={classes.questionText}>
            <RenderHTML content={questionText ?? ""} />
            {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
          </div>
        </FormLabel>
      </FormControl>

      <TextAreaComponent
        id={textareaId}
        value={commentValue}
        onChange={handleCommentChange}
        placeholder={questionHint || ""}
        rows={5}
        className={showMandatoryError && isMandatory ? classes.textareaError : ""}
        aria-describedby={errorIds || undefined}
      />

      {(isMandatory && (!commentValue || commentValue.trim() === "")) ||
      (showMandatoryError && isMandatory) ? (
        <ErrorMessage id={mandatoryErrorId}>
          <RenderHTML content={`${questionText ?? ""} ${ErrorMessages.InputRequired}`} />
        </ErrorMessage>
      ) : null}

      {showMinCharError && minCharacters && (
        <ErrorMessage id={minCharErrorId}>
          <RenderHTML
            content={`${questionText ?? ""} ${t("survey_learner_comment_min_character_message", {
              min_character: minCharacters.toString(),
            })}`}
          />
        </ErrorMessage>
      )}

      {showMaxCharError && maxCharacters && (
        <ErrorMessage id={maxCharErrorId}>
          <RenderHTML
            content={`${questionText ?? ""} ${t("survey_learner_comment_max_character_message", {
              max_character: maxCharacters.toString(),
            })}`}
          />
        </ErrorMessage>
      )}
    </div>
  );
};
